import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { useAuth } from "@/auth";
import { Button } from "@/components/catalyst/Button";
import {
	ErrorMessage,
	Field,
	FieldGroup,
	Fieldset,
	Label,
} from "@/components/catalyst/Fieldset";
import { Select } from "@/components/catalyst/Select";
import { Text } from "@/components/catalyst/Text";
import { Textarea } from "@/components/catalyst/Textarea";
import {
	type SupportTicketInput,
	useSupportApi,
} from "@/hooks/use-support-api";
import { TurnstileProvider, useTurnstile } from "@/hooks/use-turnstile";

const SupportFormSchema = z.object({
	category: z.enum(["billing", "partner", "referral", "bug", "other"], {
		message: "Please select a category",
	}),
	message: z.string().trim().min(1, "Please describe your issue"),
});

type SupportFormValues = z.infer<typeof SupportFormSchema>;

const CATEGORIES = [
	{ value: "billing", label: "Billing" },
	{ value: "partner", label: "Partner" },
	{ value: "referral", label: "Referral" },
	{ value: "bug", label: "Bug Report" },
	{ value: "other", label: "Other" },
] as const;

export function Support() {
	return (
		<TurnstileProvider>
			<SupportForm />
		</TurnstileProvider>
	);
}

function SupportForm() {
	const { user } = useAuth();
	const supportMutation = useSupportApi();
	const { token, reset: resetTurnstile } = useTurnstile();

	const {
		register,
		handleSubmit,
		resetField,
		formState: { errors },
	} = useForm<SupportFormValues>({
		resolver: zodResolver(SupportFormSchema),
		defaultValues: {
			category: undefined,
			message: "",
		},
	});

	const onSubmit = (data: SupportFormValues) => {
		const payload: SupportTicketInput = {
			...data,
			email: user.email,
			turnstileToken: token ?? undefined,
			environment: import.meta.env.DEV ? "dev" : "prod",
		};

		supportMutation.mutate(payload, {
			onSuccess: () => {
				toast.success(
					"Your message has been sent. We'll get back to you soon.",
				);
				resetField("message");
				resetTurnstile();
			},
			onError: () => {
				toast.error("Failed to send your message. Please try again.");
				resetTurnstile();
			},
		});
	};

	return (
		<div className="max-w-lg">
			<a
				href="https://docs.biteasy.co"
				target="_blank"
				rel="noopener noreferrer"
				className="mb-6 flex items-center justify-between rounded-lg border border-purple-500/20 bg-linear-to-b from-purple-500/5 via-base-200/50 to-base-200/30 p-4 transition-colors hover:from-purple-500/10"
			>
				<div>
					<p className="text-sm font-medium text-zinc-950 dark:text-white">
						Browse the docs
					</p>
					<p className="text-sm text-zinc-500 dark:text-zinc-400">
						Find answers in our documentation
					</p>
				</div>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					viewBox="0 0 20 20"
					fill="currentColor"
					className="size-5 text-zinc-400"
					aria-hidden="true"
				>
					<path
						fillRule="evenodd"
						d="M5.22 14.78a.75.75 0 0 0 1.06 0l7.22-7.22v5.69a.75.75 0 0 0 1.5 0v-7.5a.75.75 0 0 0-.75-.75h-7.5a.75.75 0 0 0 0 1.5h5.69l-7.22 7.22a.75.75 0 0 0 0 1.06Z"
						clipRule="evenodd"
					/>
				</svg>
			</a>

			<Text className="mb-6">
				Can't find what you're looking for? Send us a message and we'll get back
				to you as soon as we can.
			</Text>

			<form onSubmit={handleSubmit(onSubmit)}>
				<Fieldset>
					<FieldGroup>
						<Field>
							<Label>Category</Label>
							<Select {...register("category")}>
								<option value="" disabled>
									Select a category
								</option>
								{CATEGORIES.map((cat) => (
									<option key={cat.value} value={cat.value}>
										{cat.label}
									</option>
								))}
							</Select>
							{errors.category && (
								<ErrorMessage>{errors.category.message}</ErrorMessage>
							)}
						</Field>

						<Field>
							<Label>Message</Label>
							<Textarea
								{...register("message")}
								placeholder="Describe your issue..."
								rows={5}
							/>
							{errors.message && (
								<ErrorMessage>{errors.message.message}</ErrorMessage>
							)}
						</Field>
					</FieldGroup>
				</Fieldset>

				<div className="mt-6">
					<Button type="submit" disabled={supportMutation.isPending}>
						{supportMutation.isPending ? "Sending..." : "Send Message"}
					</Button>
				</div>
			</form>
		</div>
	);
}
